import {RRLogisticType} from "./RRLogisticType";
import {Alert, Button, Col, FormSelect, ListGroup, Modal, Row, Spinner, Tab, Tabs} from "react-bootstrap";
import {useEffect, useMemo, useState, useRef} from "react";
import ReactDatePicker from "react-datepicker";
import {RRTruckType} from "../../types/RRTruckType";
import {CancelIconButton, CheckIconButton, ConfirmIconButton, DeleteIconButton} from "../../components/IconButtons";
import {dateTimeToString, dateToString, showErrorsListInToast, timeDiffToString} from "../../tools";
import {RREmployeeType} from "../../types/RREmployeeType";
import {saveTransportMutation, transportMutationError} from "./transportMutations";
import {transportPeriod, periodsOverlap} from "./transportValidation";
import RoadBooksView from "../reports/RoadBooksView";

interface LogisticDetailsProps {
    logistic: RRLogisticType,
    trucks: RRTruckType[],
    drivers: RREmployeeType[],
    logistics: RRLogisticType[],
    calendarPeriodFrom: Date,
    calendarPeriodTo: Date,
    onDone: () => void,
    validateLogistic?: (logistic: RRLogisticType) => string[]
}

function hasOverlappingPeriod(first: RRLogisticType, second: RRLogisticType): boolean {
    return (["plan", "report"] as const).some(mode => {
        const a = transportPeriod(first, mode);
        const b = transportPeriod(second, mode);
        return a !== null && b !== null && periodsOverlap(a, b);
    });
}

export default function LogisticDetails({
    logistic,
    trucks,
    drivers,
    logistics,
    calendarPeriodFrom,
    calendarPeriodTo,
    onDone,
    validateLogistic
}: LogisticDetailsProps) {
    const [editedLogistic, setEditedLogistic] = useState<RRLogisticType>({...logistic});
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [showDriverChangeDialog, setShowDriverChangeDialog] = useState<boolean>(false);
    const [showRoadBooksDialog, setShowRoadBooksDialog] = useState<boolean>(false);
    const [savingDriverChange, setSavingDriverChange] = useState<boolean>(false);
    const pendingWrite = useRef(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setEditedLogistic({...logistic});
    }, [logistic]);

    const availableDrivers = useMemo(() => {
        return drivers
            .filter(driver => {
                if (!driver.id ||
                    driver.id === editedLogistic.driverId ||
                    driver.isDeleted ||
                    driver.startDate > editedLogistic.startDate ||
                    (driver.leaveDate !== null &&
                        driver.leaveDate !== undefined &&
                        driver.leaveDate < editedLogistic.startDate)) {
                    return false;
                }

                return !logistics.some(existingLogistic =>
                    existingLogistic.id !== editedLogistic.id &&
                    existingLogistic.driverId === driver.id &&
                    hasOverlappingPeriod(editedLogistic, existingLogistic)
                );
            })
            .sort((firstDriver, secondDriver) => firstDriver.name.localeCompare(secondDriver.name, "bg"));
    }, [drivers, editedLogistic, logistics]);

    const canOpenRoadBooks = editedLogistic.dayType === "WORKDAY" &&
        !editedLogistic.workInStore &&
        editedLogistic.isOwn &&
        Boolean(editedLogistic.truck.id || editedLogistic.truck.plate.trim());


    async function save() {
        if (pendingWrite.current) return;
        if (validateLogistic) {
            const errors = validateLogistic(editedLogistic);
            if (errors.length > 0) {
                showErrorsListInToast(
                    "Грешки",
                    errors
                )
                return;
            }
        }

        pendingWrite.current = true;
        setSaving(true);
        try {
            await saveTransportMutation({
                action: "planSave",
                logisticId: editedLogistic.id,
                source: logistic,
                logistic: editedLogistic
            });

            onDone();
        } catch (e: unknown) {
            console.error("Error saving logistic:", e);
            alert("Грешка при запазване на логистиката: " + transportMutationError(e));
        } finally {
            pendingWrite.current = false;
            setSaving(false);
        }
    }

    async function doDelete() {
        if (pendingWrite.current) return;
        if (!editedLogistic.id) {
            alert("Няма логистика за изтриване.");
            return;
        }

        pendingWrite.current = true;
        setSaving(true);
        try {
            await saveTransportMutation({action: "delete", logisticId: editedLogistic.id, source: logistic});
            onDone();
        } catch (error) {
            console.error("Error deleting logistic:", error);
            const message = transportMutationError(error);
            alert("Грешка при изтриване на логистиката: " + message);
        } finally {
            pendingWrite.current = false;
            setSaving(false);
        }
    }

    async function changeDriver(newDriver: RREmployeeType) {
        if (pendingWrite.current) return;
        if (!editedLogistic.id || !newDriver.id) {
            return;
        }

        setSavingDriverChange(true);
        pendingWrite.current = true;
        setSaving(true);

        try {
            await saveTransportMutation({
                action: "changeDriver",
                logisticId: editedLogistic.id,
                expectedRevision: logistic.revision ?? 0,
                source: logistic,
                driver: {id: newDriver.id, name: newDriver.name}
            });

            onDone();
        } catch (error: unknown) {
            const message = transportMutationError(error);
            console.error("Error changing logistics driver:", error);
            alert("Грешка при смяна на служител: " + message);
        } finally {
            setSavingDriverChange(false);
            pendingWrite.current = false;
            setSaving(false);
        }
    }

    return (
        <>
            <Modal show={!showRoadBooksDialog} onHide={onDone}>
                <Modal.Header closeButton>
                    <Modal.Title>{editedLogistic.driverName} - <span
                        className={"text-primary"}>{dateToString(editedLogistic.startDate)}</span></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Tabs
                        activeKey={editedLogistic.dayType === "WORKDAY" ? "WORKDAY" : "HOLIDAY"}
                        onSelect={(k) => {
                            setEditedLogistic({
                                ...editedLogistic,
                                dayType: k as RRLogisticType["dayType"]
                            });
                        }}
                        className="mb-3"
                    >
                        <Tab eventKey="WORKDAY" title="Работен ден"/>
                        <Tab eventKey="HOLIDAY" title="Почивен ден"/>
                    </Tabs>

                    <fieldset disabled={saving} className={"p-2"}>
                        {
                            editedLogistic.dayType === "WORKDAY" && <>
                                <Row className={"mb-2"}>
                                    <Col xs={4}>Начална дата / час:</Col>
                                    <Col>
                                        <ReactDatePicker
                                            selected={editedLogistic.startDate}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    setEditedLogistic({
                                                        ...editedLogistic,
                                                        startDate: date,
                                                        endDate: date > editedLogistic.endDate ? date : editedLogistic.endDate
                                                    });
                                                }
                                            }}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            dateFormat="dd.MM.yyyy HH:mm"
                                            className="form-control"
                                        />
                                    </Col>
                                </Row>

                                <Row className={"mb-2"}>
                                    <Col xs={4}>Крайна дата / час:</Col>
                                    <Col>
                                        <ReactDatePicker
                                            selected={editedLogistic.endDate}
                                            onChange={(date: Date | null) => {
                                                if (date) {
                                                    setEditedLogistic({
                                                        ...editedLogistic,
                                                        endDate: date
                                                    });
                                                }
                                            }}
                                            showTimeSelect
                                            timeFormat="HH:mm"
                                            timeIntervals={15}
                                            dateFormat="dd.MM.yyyy HH:mm"
                                            className="form-control"
                                            minDate={editedLogistic?.startDate}
                                        />
                                    </Col>
                                </Row>

                                <Row className={"mb-2"}>
                                    <Col xs={4}>Работа в склад:</Col>
                                    <Col>
                                        <FormSelect
                                            value={editedLogistic.workInStore ? "true" : "false"}
                                            onChange={(e) => {
                                                setEditedLogistic({
                                                    ...editedLogistic,
                                                    workInStore: e.target.value === "true"
                                                });
                                            }}
                                        >
                                            <option value="true">Да</option>
                                            <option value="false">Не</option>
                                        </FormSelect>
                                    </Col>
                                </Row>

                                {
                                    !editedLogistic.workInStore &&
                                    <>
                                        <Row className={"mb-2"}>
                                            <Col xs={4}>Номер на събитие:</Col>
                                            <Col>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={editedLogistic.eventNumber}
                                                    onChange={(e) => {
                                                        setEditedLogistic({
                                                            ...editedLogistic,
                                                            eventNumber: e.target.value
                                                        });
                                                    }}
                                                />
                                            </Col>
                                        </Row>

                                        <Row className={"mb-2"}>
                                            <Col xs={4}>Собствен транспорт:</Col>
                                            <Col>
                                                <FormSelect
                                                    value={editedLogistic.isOwn ? "true" : "false"}
                                                    onChange={(e) => {
                                                        setEditedLogistic({
                                                            ...editedLogistic,
                                                            isOwn: e.target.value === "true"
                                                        });
                                                    }}
                                                >
                                                    <option value="true">Да</option>
                                                    <option value="false">Не</option>
                                                </FormSelect>
                                            </Col>
                                        </Row>

                                        {
                                            editedLogistic.isOwn && <>
                                                <Row className={"mb-2"}>
                                                    <Col xs={4}>Автомобил:</Col>
                                                    <Col>
                                                        <FormSelect
                                                            value={editedLogistic.truck?.id || ""}
                                                            onChange={(e) => {
                                                                const selectedTruck = trucks.find(t => t.id === e.target.value);
                                                                if (selectedTruck) {
                                                                    setEditedLogistic({
                                                                        ...editedLogistic,
                                                                        truck: selectedTruck
                                                                    });
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Изберете автомобил</option>
                                                            {trucks.map(truck => (
                                                                <option key={truck.id} value={truck.id}>
                                                                    {truck.name} ({truck.plate})
                                                                </option>
                                                            ))}
                                                        </FormSelect>
                                                    </Col>
                                                </Row>
                                                <Row className={"mb-2"}>
                                                    <Col xs={4}>Начална точка:</Col>
                                                    <Col>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editedLogistic.startPoint}
                                                            onChange={(e) => {
                                                                setEditedLogistic({
                                                                    ...editedLogistic,
                                                                    startPoint: e.target.value
                                                                });
                                                            }}
                                                            placeholder="Въведете начална точка"
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className={"mb-2"}>
                                                    <Col xs={4}>Крайна точка:</Col>
                                                    <Col>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editedLogistic.endPoint}
                                                            onChange={(e) => {
                                                                setEditedLogistic({
                                                                    ...editedLogistic,
                                                                    endPoint: e.target.value
                                                                });
                                                            }}
                                                            placeholder="Въведете крайна точка"
                                                        />
                                                    </Col>
                                                </Row>
                                            </>
                                        }

                                        {
                                            !editedLogistic.isOwn &&
                                            <>
                                                <Row className={"mb-2"}>
                                                    <Col xs={4}>Автомобил:</Col>
                                                    <Col>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editedLogistic.truck?.name || ""}
                                                            onChange={(e) => {
                                                                setEditedLogistic({
                                                                    ...editedLogistic,
                                                                    truck: {
                                                                        ...editedLogistic.truck,
                                                                        name: e.target.value
                                                                    }
                                                                });
                                                            }}
                                                            placeholder="Въведете име на автомобила"
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row className={"mb-2"}>
                                                    <Col xs={4}>Рег. номер:</Col>
                                                    <Col>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            value={editedLogistic.truck?.plate || ""}
                                                            onChange={(e) => {
                                                                setEditedLogistic({
                                                                    ...editedLogistic,
                                                                    truck: {
                                                                        ...editedLogistic.truck,
                                                                        plate: e.target.value
                                                                    }
                                                                });
                                                            }}
                                                            placeholder="Въведете регистрационен номер"
                                                        />
                                                    </Col>
                                                </Row>
                                            </>
                                        }
                                    </>
                                }
                            </>
                        }

                        {
                            editedLogistic.dayType !== "WORKDAY" && <>
                                <Row>
                                    <Col xs={4}>Причина:</Col>
                                    <Col>
                                        <FormSelect
                                            value={editedLogistic.dayType}
                                            onChange={(e) => {
                                                setEditedLogistic({
                                                    ...editedLogistic,
                                                    dayType: e.target.value as RRLogisticType["dayType"]
                                                });
                                            }}
                                        >
                                            <option value="HOLIDAY">Планирана отпуска</option>
                                            <option value="SICK">Отсъства по болест</option>
                                            <option value="DAYOFF">По други причини</option>
                                        </FormSelect>
                                    </Col>
                                </Row>
                            </>
                        }

                        <Row className={"mb-2 mt-2"}>
                            <Col xs={4}>Бележки:</Col>
                            <Col>
                            <textarea
                                className="form-control"
                                rows={3}
                                value={editedLogistic.planNotes}
                                onChange={(e) => {
                                    setEditedLogistic({
                                        ...editedLogistic,
                                        planNotes: e.target.value
                                    });
                                }}
                            />
                            </Col>
                        </Row>

                    </fieldset>
                </Modal.Body>
                <Modal.Footer>
                    <Col xs={"auto"} className={"d-flex gap-2"}>
                        {
                            editedLogistic.id && editedLogistic.dayType === "WORKDAY" &&
                            <Button disabled={saving} variant={"outline-primary"} onClick={() => setShowDriverChangeDialog(true)}>
                                Смени служител
                            </Button>
                        }
                        {
                            canOpenRoadBooks &&
                            <Button
                                disabled={saving}
                                variant={"outline-primary"}
                                onClick={() => setShowRoadBooksDialog(true)}
                            >
                                Пътни книжки
                            </Button>
                        }
                    </Col>
                    <Col className={"text-start"}>
                        {
                            editedLogistic.id &&
                            <>
                                {
                                    confirmDelete ?
                                        <ConfirmIconButton
                                            onClick={doDelete}
                                            onReject={
                                                () => setConfirmDelete(false)
                                            }
                                        /> :
                                        <DeleteIconButton onClick={
                                            () => setConfirmDelete(true)
                                        }/>
                                }
                            </>
                        }
                    </Col>
                    <Col className={"fw-bold"}>
                        Работни часове: &nbsp;
                        {
                            timeDiffToString(
                                editedLogistic.startDate,
                                editedLogistic.endDate,
                                0
                            )
                        }
                    </Col>
                    <Col className={"text-end"}>
                        <CheckIconButton title="Запиши" disabled={saving} onClick={save}/>&nbsp;
                        <CancelIconButton onClick={onDone}/>
                    </Col>
                </Modal.Footer>
            </Modal>

            <Modal show={showDriverChangeDialog} onHide={() => setShowDriverChangeDialog(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Смени служител</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>
                        Свободни шофьори за периода {dateTimeToString(editedLogistic.startDate)} – {dateTimeToString(editedLogistic.endDate)}:
                    </p>
                    {
                        availableDrivers.length === 0 ?
                            <Alert variant={"warning"} className={"mb-0"}>
                                Няма свободен шофьор за избрания период.
                            </Alert>
                            :
                            <ListGroup>
                                {
                                    availableDrivers.map(driver => (
                                        <ListGroup.Item
                                            key={driver.id}
                                            className={"d-flex justify-content-between align-items-center"}
                                        >
                                            <span>{driver.name}</span>
                                            <Button
                                                size={"sm"}
                                                disabled={savingDriverChange}
                                                onClick={() => changeDriver(driver)}
                                            >
                                                {
                                                    savingDriverChange ? <Spinner size={"sm"}/> : "Избери"
                                                }
                                            </Button>
                                        </ListGroup.Item>
                                    ))
                                }
                            </ListGroup>
                    }
                </Modal.Body>
            </Modal>

            <Modal
                show={showRoadBooksDialog}
                onHide={() => setShowRoadBooksDialog(false)}
                size={"xl"}
                fullscreen={"lg-down"}
                scrollable
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Пътни книжки</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <RoadBooksView
                        initialPeriodFrom={calendarPeriodFrom}
                        initialPeriodTo={calendarPeriodTo}
                        embedded
                    />
                </Modal.Body>
            </Modal>
        </>
    );
}
